import { NgModule } from '@angular/core';
import { PreloadAllModules, RouterModule, Routes } from '@angular/router';
import { BusinessProfileDashboardGuard } from './core/guards/onboarding/business-profile-dashboard.guard';
import { StateResolver } from './core/resolvers/common/state.resolver';
import { DocumentResolver } from './core/resolvers/document/document.resolver';
import { LoginResolver } from './core/resolvers/login/login.resolver';
import { BusinessIndustryResolver } from './core/resolvers/onboarding/business-profile/business-industry.resolver';
import { BusinessInvoiceResolver } from './core/resolvers/onboarding/business-profile/business-invoice.resolver';
import { EmployeeCountResolver } from './core/resolvers/onboarding/business-profile/employee-count.resolver';
import { HeardAboutUsResolver } from './core/resolvers/onboarding/business-profile/heard-about-us.resolver';
import { RescoreEligibilityResolver } from './core/resolvers/onboarding/business-profile/rescore-eligibility.resolver';
import { SupportMethodResolver } from './core/resolvers/onboarding/business-profile/support-method.resolver';
import { PageStateClientAdditionalInfoRequestPersonalizedResolver } from './core/resolvers/onboarding/navigation/page-state-client-additional-info-request-personalized.resolver';
import { PageStateClientPersonalizedResolver } from './core/resolvers/onboarding/navigation/page-state-client-personalized.resolver';
import { PageStateResolver } from './core/resolvers/onboarding/navigation/page-state.resolver';
import { ForgotpasswordComponent } from './pages/login/forgotpassword/forgotpassword.component';
import { LoginComponent } from './pages/login/login/login.component';
import { RememberMeComponent } from './pages/login/remember-me/remember-me.component';
import { ResetpasswordComponent } from './pages/login/resetpassword/resetpassword.component';
import { SetPhonenumberComponent } from './pages/login/set-phonenumber/set-phonenumber.component';
import { VerificationComponent } from './pages/login/verification/verification.component';
import { VerifycodeComponent } from './pages/login/verifycode/verifycode.component';
import { VerifyphonecodeComponent } from './pages/login/verifyphonecode/verifyphonecode.component';
import { VerifyuserComponent } from './pages/login/verifyuser/verifyuser.component';
import { ClientAdditionalInfoRequestDashboardComponent } from './pages/onboarding/dashboards/client-additional-info-request-dashboard/client-additional-info-request-dashboard.component';
import { OnboardingDashboardComponent } from './pages/onboarding/dashboards/dashboard/dashboard.component';
import { DisqualifiedDashboardComponent } from './pages/onboarding/dashboards/disqualified-dashboard/disqualified-dashboard.component';
import { MsaDashboardComponent } from './pages/onboarding/dashboards/msa-dashboard/msa-dashboard.component';
import { NowaccountActivateDashboardComponent } from './pages/onboarding/dashboards/nowaccount-activate-dashboard/nowaccount-activate-dashboard.component';
import { DocumentsComponent } from './pages/onboarding/documents/documents.component';
import { LegacyUpgradeNavigatorComponent } from './pages/onboarding/legacy-upgrade-navigator/legacy-upgrade-navigator.component';
import { LegacyUpgradeComponent } from './pages/onboarding/legacy-upgrade/legacy-upgrade.component';
import { PreDashboardComponent } from './pages/onboarding/pre-dashboard/pre-dashboard.component';
import { RedirectComponent } from './pages/onboarding/redirect/redirect.component';

const routes: Routes = [
  {
    path: '',
    loadChildren: () =>
      import('./pages/login/login.module').then((m) => m.LoginModule),
  },
  {
    path: '',
    loadChildren: () =>
      import('./pages/nowaccount/client/client.module').then(
        (m) => m.ClientModule,
      ),
  },
  {
    path: '',
    loadChildren: () =>
      import('./pages/nowaccount/internal/internal.module').then(
        (m) => m.InternalModule,
      ),
  },
  {
    path: 'Clients/Registrations',
    loadChildren: () =>
      import('./pages/nowaccount/client/client.module').then(
        (m) => m.ClientModule,
      ),
  },
  {
    path: 'Clients/Registrations',
    loadChildren: () =>
      import('./pages/nowaccount/internal/internal.module').then(
        (m) => m.InternalModule,
      ),
  },
  { path: 'verification', component: VerificationComponent },
  { path: 'verifyuser', component: VerifyuserComponent },
  { path: 'verifycode', component: VerifycodeComponent },
  {
    path: 'onboarding/dashboard',
    component: OnboardingDashboardComponent,
    resolve: { data: PageStateClientPersonalizedResolver },
    canActivate: [BusinessProfileDashboardGuard],
  },
  {
    path: 'onboarding/disqualified-dashboard',
    component: DisqualifiedDashboardComponent,
    resolve: {
      data: PageStateResolver,
      rescoreEligibility: RescoreEligibilityResolver,
    },
  },
  {
    path: 'onboarding/msa-dashboard',
    component: MsaDashboardComponent,
    resolve: { data: PageStateClientPersonalizedResolver },
  },
  {
    path: 'onboarding/nowaccount-activate-dashboard',
    component: NowaccountActivateDashboardComponent,
    resolve: { data: PageStateClientPersonalizedResolver },
  },
  {
    path: 'onboarding/client-additional-info-request-dashboard',
    component: ClientAdditionalInfoRequestDashboardComponent,
    resolve: { data: PageStateClientAdditionalInfoRequestPersonalizedResolver },
  },
  {
    path: 'onboarding/documents',
    component: DocumentsComponent,
    resolve: { data: DocumentResolver },
  },
  {
    path: 'onboarding/legacy-upgrade',
    component: LegacyUpgradeComponent,
    resolve: {
      data: PageStateResolver,
      heardAboutUsOptions: HeardAboutUsResolver,
      businessIndustryOptions: BusinessIndustryResolver,
      stateProvinceOptions: StateResolver,
      numberOfEmployeesOptions: EmployeeCountResolver,
      supportMethodOptions: SupportMethodResolver,
      businessInvoiceOptions: BusinessInvoiceResolver,
    },
  },
  {
    path: 'create-account',
    loadChildren: () =>
      import('./pages/onboarding/create-account/create-account.module').then(
        (m) => m.CreateAccountModule,
      ),
  },
  {
    path: 'business-profile',
    loadChildren: () =>
      import(
        './pages/onboarding/business-profile/business-profile.module'
      ).then((m) => m.BusinessProfileModule),
  },
  {
    path: 'financial-profile',
    loadChildren: () =>
      import(
        './pages/onboarding/financial-profile/financial-profile.module'
      ).then((m) => m.FinancialProfileModule),
  },
  {
    path: 'customer-portfolio',
    loadChildren: () =>
      import(
        './pages/onboarding/customer-portfolio/customer-portfolio.module'
      ).then((m) => m.CustomerPortfolioModule),
  },

  { path: 'forgotpassword', component: ForgotpasswordComponent },
  { path: 'remember-me', component: RememberMeComponent },
  { path: 'set-phonenumber', component: SetPhonenumberComponent },
  { path: 'verifyphonecode', component: VerifyphonecodeComponent },
  { path: 'redirect', component: RedirectComponent },

  // This should be removed after the creating indepent angular
  {
    path: '',
    component: LoginComponent,
    resolve: { data: LoginResolver },
  },
  {
    path: 'OnBoarding/Login/Login',
    component: LoginComponent,
    resolve: { data: LoginResolver },
  },
  { path: 'OnBoarding/Login/Verification', component: VerificationComponent },
  {
    path: 'OnBoarding/OnBoarding/PreDashBoard',
    component: PreDashboardComponent,
  },
  {
    path: 'pre-dashboard',
    component: PreDashboardComponent,
  },
  {
    path: 'OnBoarding/OnBoarding/LegacyUpgrade',
    component: LegacyUpgradeNavigatorComponent,
  },
  {
    path: 'OnBoarding/OnBoarding/Index',
    component: OnboardingDashboardComponent,
    resolve: { data: PageStateResolver },
    canActivate: [BusinessProfileDashboardGuard],
  },
  {
    path: 'OnBoarding/OnBoarding',
    component: OnboardingDashboardComponent,
    resolve: { data: PageStateResolver },
    canActivate: [BusinessProfileDashboardGuard],
  },
  { path: 'OnBoarding/Login/ResetPassword', component: ResetpasswordComponent },
  {
    path: 'OnBoarding/OnBoarding/Redirect',
    component: RedirectComponent,
    resolve: { data: LoginResolver },
  },

  {
    path: 'admin',
    loadChildren: () =>
      import('./pages/nowaccount/admin/admin.module').then(
        (m) => m.AdminModule,
      ),
  },
  {
    path: '',
    loadChildren: () =>
      import('./pages/nowaccount/admin/admin.module').then(
        (m) => m.AdminModule,
      ),
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, { preloadingStrategy: PreloadAllModules }),
  ],
  exports: [RouterModule],
  providers: [PageStateResolver],
})
export class AppRoutingModule {}
